import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { Menu, X, Phone, ArrowRight } from "lucide-react";
import { BrandLogo } from "@/components/brand/BrandLogo";
import { NAV, SITE } from "@/lib/content";
import { EASE } from "@/components/site/Motion";

const slug = (s: string) => s.toLowerCase().replace(/\s+/g, "-");

export function TopBar() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => { document.body.style.overflow = ""; };
  }, [open]);

  return (
    <header data-testid="site-header" className={`sticky top-0 z-50 border-b transition-[background-color,border-color,padding] duration-500 ${scrolled || open ? "border-cream/10 bg-forest-night/85 py-2.5 backdrop-blur-xl" : "border-transparent bg-forest-deep py-4"}`}>
      <div className="wrap flex items-center justify-between gap-6">
        <BrandLogo />
        <nav className="hidden items-center gap-6 xl:flex" aria-label="Main">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.to === "/"} data-testid={`nav-${slug(n.label)}`} className={({ isActive }) => `link-line text-sm font-medium ${isActive ? "text-gold-soft" : "text-cream/85 hover:text-cream"}`}>
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-2.5">
          <a href={`tel:${SITE.phoneTel}`} data-testid="header-call-link" aria-label={`Call ${SITE.phoneDisplay}`} className="icon-ring hidden h-10 w-10 transition-colors duration-300 hover:bg-gold hover:text-forest-deep sm:grid">
            <Phone className="h-4 w-4" />
          </a>
          <Link to="/contact#book" data-testid="header-book-button" className="btn-gold group hidden !px-5 !py-2.5 sm:inline-flex">
            Free Consultation <ArrowRight className="h-4 w-4 transition-transform duration-500 group-hover:translate-x-1" />
          </Link>
          <button data-testid="mobile-menu-toggle" onClick={() => setOpen((v) => !v)} aria-label={open ? "Close menu" : "Open menu"} className="grid h-11 w-11 place-items-center rounded-full border border-cream/20 text-cream xl:hidden">
            {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
          </button>
        </div>
      </div>
      <AnimatePresence>{open && <MobileMenu />}</AnimatePresence>
    </header>
  );
}

function MobileMenu() {
  return (
    <motion.div
      data-testid="mobile-menu"
      initial={{ clipPath: "inset(0 0 100% 0)" }}
      animate={{ clipPath: "inset(0 0 0% 0)" }}
      exit={{ clipPath: "inset(0 0 100% 0)" }}
      transition={{ duration: 0.7, ease: EASE }}
      className="grain fixed inset-x-0 bottom-0 top-[64px] z-40 overflow-y-auto bg-forest-night px-6 pb-32 pt-8 text-cream xl:hidden"
    >
      <ul className="space-y-1">
        {NAV.map((n, i) => (
          <motion.li key={n.to} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }} transition={{ delay: 0.12 + i * 0.05, duration: 0.6, ease: EASE }}>
            <NavLink to={n.to} end={n.to === "/"} data-testid={`mobile-nav-${slug(n.label)}`} className={({ isActive }) => `flex items-baseline justify-between border-b border-cream/10 py-4 ${isActive ? "text-gold-soft" : ""}`}>
              <span className="font-display text-4xl">{n.label}</span>
              <span className="text-xs text-cream/70">0{i + 1}</span>
            </NavLink>
          </motion.li>
        ))}
      </ul>
      <p className="mt-8 text-sm text-cream/80">{SITE.coachLine}</p>
      <Link to="/contact#book" data-testid="mobile-menu-book-button" className="btn-gold mt-5 w-full">Book your free consultation</Link>
    </motion.div>
  );
}
